/**
 * Evidence card for the claim detail page.
 *
 * Single source of truth for every file linked to the claim - session
 * uploads (consumer / contractor / third-party drops on the evidence
 * request page) plus direct admin uploads. Replaces the older split
 * between an "Evidence" capture-field list and an "Attachments" card.
 *
 * Each row shows: thumbnail (or filetype icon), filename, category,
 * uploader chip, timestamp, and preview / download / delete buttons.
 * Preview opens images inline in a dialog; non-images fall back to a
 * new-tab download.
 *
 * Soft-deleted files aren't shown by default. A "Show deleted" toggle
 * reveals tombstones (rendered greyed out with deletion attribution).
 */

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Check,
  ChevronDown,
  Download,
  Image as ImageIcon,
  Loader2,
  Plus,
} from 'lucide-react'

import {
  useClaimAttachments,
  useDeleteAttachment,
  useDocumentCategories,
  useRecategoriseAttachment,
  type Attachment,
} from '@/admin/pages/claims/use-claim-attachments'
import { useRemoveClaimEvidence } from '@/admin/hooks/use-claims'
import { AddEvidenceDialog } from '@/admin/components/claims/add-evidence-dialog'
import { DetailCard } from '@/admin/components/shared/detail-card'
import { usePermissions } from '@/admin/hooks/use-permissions'
import { useFormatters } from '@/shared/lib/formatting'
import api from '@/admin/lib/api'
import { toastApiError } from '@/shared/lib/api-error'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  FileViewerDialog,
  type ViewableFile,
} from '@/shared/components/file-viewer-dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  useClaimChecklist,
  useEvidenceAnnotations,
  type ClaimChecklist,
  type EvidenceAnnotation,
} from '@/admin/hooks/use-lab-tasks'
import { PhotoLightbox } from '@/admin/components/claims/photo-lightbox'
import { useFeatureFlag, FLAGS } from '@/admin/hooks/use-feature-flag'
import { isImageValue } from '@/shared/lib/file-types'
import {
  MEDIA_GROUP_PREFIX,
  evidenceGroupLabel,
  normaliseCategoryKey,
  resolveEvidenceCategoryKey,
} from '@/admin/components/claims/evidence/evidence-grouping'
import { EvidenceFileRow } from '@/shared/components/evidence/evidence-file-row'
import { formatBytes } from '@/shared/components/evidence/evidence-file-utils'
import { AuthenticatedImage } from '@/admin/components/authenticated-image'
import { cn } from '@/shared/lib/utils'

const RECIPIENT_LABEL: Record<string, string> = {
  consumer: 'Consumer',
  contractor: 'Contractor',
  third_party: 'Third party',
  staff: 'Staff',
}

// Uploader attribution stays on each row (see EvidenceRow) - it is just no
// longer what the card groups by (WPS-140 #1).

/**
 * Where an evidence row lives, so its delete routes to the right endpoint:
 * - `registry`: a UserUploadedFile attachment (soft-delete via useDeleteAttachment)
 * - `attribute`: a file captured on claim.attribute_values (removed via the
 *   WP1-63 useRemoveClaimEvidence endpoint).
 */
type EvidenceSource = 'registry' | 'attribute'

interface EvidenceRowItem {
  att: Attachment
  deletable: boolean
  source: EvidenceSource
}

interface CategoryGroup {
  key: string
  label: string
  /** The category is one the claim's checklist asks for. */
  isRequired: boolean
  /** A reviewer has signed the category off (WPS-140 #3). */
  isApproved: boolean
  rows: EvidenceRowItem[]
}

/**
 * Bucket rows by evidence category (WPS-140 #1).
 *
 * Ordered to match the checklist - which is the order the claimant was asked
 * for the evidence in - then any category the packet does not define, then the
 * media-type fallback groups last, since those are the ones nobody classified.
 */
function groupRowsByCategory(
  rows: EvidenceRowItem[],
  checklist: ClaimChecklist | undefined,
): CategoryGroup[] {
  const items = checklist?.items ?? []
  const photoCategories = checklist?.all_photo_categories ?? []

  const labels = new Map<string, string>()
  const required = new Set<string>()
  const approved = new Set<string>()
  const order = new Map<string, number>()

  // Checklist items first so their order wins; the wider photo catalogue
  // supplies labels for a category this claim's defect does not require.
  items.forEach((item, index) => {
    const key = normaliseCategoryKey(item.category || item.key)
    if (!key) return
    if (item.label) labels.set(key, item.label)
    if (item.is_required) required.add(key)
    if (item.approved) approved.add(key)
    if (!order.has(key)) order.set(key, index)
  })
  photoCategories.forEach((item) => {
    const key = normaliseCategoryKey(item.category || item.key)
    if (key && item.label && !labels.has(key)) labels.set(key, item.label)
  })

  const byKey = new Map<string, CategoryGroup>()
  for (const row of rows) {
    const key = resolveEvidenceCategoryKey(row.att)
    let group = byKey.get(key)
    if (!group) {
      group = {
        key,
        label: evidenceGroupLabel(key, labels),
        isRequired: required.has(key),
        isApproved: approved.has(key),
        rows: [],
      }
      byKey.set(key, group)
    }
    group.rows.push(row)
  }

  const rank = (group: CategoryGroup) => {
    if (group.key.startsWith(MEDIA_GROUP_PREFIX)) return 2
    return order.has(group.key) ? 0 : 1
  }
  return [...byKey.values()].sort((a, b) => {
    const byRank = rank(a) - rank(b)
    if (byRank !== 0) return byRank
    const byOrder = (order.get(a.key) ?? 0) - (order.get(b.key) ?? 0)
    if (byOrder !== 0) return byOrder
    return a.label.localeCompare(b.label)
  })
}

export function EvidenceCard({
  claimUuid,
  title = 'Evidence',
  canAdd = false,
  extraItems,
  filterToSection,
  autoOpenAdd = false,
  onAutoOpenAddHandled,
}: {
  claimUuid: string
  /**
   * Legacy props from the bare-file-picker era. The "+ Add" button now
   * opens the structured AddEvidenceDialog (category + sub-category)
   * owned by this card, so these are accepted-but-ignored to keep the
   * existing call sites compiling.
   */
  onAdd?: () => void
  isAdding?: boolean
  canAdd?: boolean
  /** Card heading - tenants can theme it (e.g. "Evidence / Proof of Purchase"). Defaults to "Evidence". */
  title?: string
  /**
   * When set, only show attachments whose document category is relevant to
   * this claim section (overview | inspection | lab | settlement | loa).
   * Drives the per-tab views (Inspection/Settlement/LOA/Lab) off the one
   * attachment registry, so a document uploaded once appears wherever its
   * category says it's relevant.
   */
  filterToSection?: string
  /**
   * Open the Add evidence dialog as soon as the card mounts. Set when the
   * reviewer arrived here from a blocked action that named a missing
   * document: landing them on the tab and leaving them to find "+ Add" is a
   * second step for a decision already made. Fires once, then calls
   * ``onAutoOpenAddHandled`` so closing the dialog cannot re-open it.
   */
  autoOpenAdd?: boolean
  /** Called once ``autoOpenAdd`` has been consumed. */
  onAutoOpenAddHandled?: () => void
  /**
   * Files captured outside the attachments registry - the claim "photos"
   * field and the admin "+" upload write to claim.attribute_values, not to
   * UserUploadedFile.subject. Pass them here so every source of evidence
   * shows in one card. Deletable via the WP1-63 remove-evidence endpoint
   * (routed by each row's `source`), same as registry attachments.
   */
  extraItems?: Attachment[]
}) {
  const { t } = useTranslation()
  const [showDeleted, setShowDeleted] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  // Consume the auto-open request exactly once. Guarded by `canAdd` so it can
  // never open a dialog for a reviewer who is not allowed to upload.
  useEffect(() => {
    if (!autoOpenAdd) return
    if (canAdd) setAddOpen(true)
    onAutoOpenAddHandled?.()
  }, [autoOpenAdd, canAdd, onAutoOpenAddHandled])
  const [downloadingAll, setDownloadingAll] = useState(false)
  const [previewing, setPreviewing] = useState<Attachment | null>(null)
  const [recategorising, setRecategorising] = useState<Attachment | null>(null)
  const [pendingDelete, setPendingDelete] = useState<{
    att: Attachment
    source: EvidenceSource
  } | null>(null)
  // Category groups the user has expanded (all collapsed by default, so the
  // card opens as a compact list of categories the user can drill into).
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(),
  )
  const query = useClaimAttachments(claimUuid, { includeDeleted: showDeleted })
  // Supplies the category labels and the order the claimant was asked for
  // them in. Absent (still loading, or a claim with no packet) the groups
  // still render, keyed off the attachment's own category.
  const { data: checklist } = useClaimChecklist(claimUuid)
  // Annotations are a lab capability; without the flag the endpoints 404, so
  // the reviewer degrades to a plain carousel rather than erroring.
  const labEnabled = useFeatureFlag(FLAGS.LAB)
  const { data: annotations = [] } = useEvidenceAnnotations(claimUuid, {
    enabled: labEnabled,
  })
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const deleteMutation = useDeleteAttachment(claimUuid)
  const recategorise = useRecategoriseAttachment(claimUuid)
  const removeEvidence = useRemoveClaimEvidence()
  const { data: categories, isLoading: isLoadingCats } = useDocumentCategories()
  const { can } = usePermissions()
  const canDelete = can('attachments.delete')
  // Re-filing has its own permission; delete rights still cover it.
  const canRecategorise = canDelete || can('attachments.recategorise')
  const isDeletingAny = deleteMutation.isPending || removeEvidence.isPending

  // category key -> the sections it's relevant to (for per-tab filtering).
  const sectionsByCategory = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const c of categories ?? []) map.set(c.key, c.sections ?? ['overview'])
    return map
  }, [categories])

  const inSection = (att: Attachment): boolean => {
    if (!filterToSection) return true
    const sections = sectionsByCategory.get(att.category) ?? ['overview']
    return sections.includes(filterToSection)
  }

  // `query.data` is normalised to an array by the hook's `select`, but guard
  // here too: `?? []` only covers null/undefined, so an unexpected non-array
  // value would throw `filter is not a function`. Array.isArray is the only
  // safe gate against a hostile payload reaching `.filter`.
  const attachments = (Array.isArray(query.data) ? query.data : []).filter(inSection)
  const liveCount = attachments.filter((i) => !i.is_deleted).length
  const deletedCount = attachments.filter((i) => i.is_deleted).length
  const rows: EvidenceRowItem[] = [
    ...attachments.map((att) => ({
      att,
      deletable: canDelete && !att.is_deleted,
      source: 'registry' as const,
    })),
    // Attribute-captured evidence (claim "photos" field, intake wizard fields,
    // admin "+" uploads that wrote to claim.attribute_values) is overview-only;
    // a section-filtered card never shows it. WP1-63 added a remove endpoint for
    // these, so they're deletable too (routed through useRemoveClaimEvidence).
    ...(filterToSection ? [] : (extraItems ?? [])).map((att) => ({
      att,
      deletable: canDelete,
      source: 'attribute' as const,
    })),
  ]
  // Group evidence by category, not by uploader (WPS-140 #1): a reviewer
  // looks for a kind of evidence, not for a person. Labels and ordering come
  // from the claim's checklist, so a category reads exactly as the claimant
  // was asked for it. Groups appear as evidence arrives - nothing is fixed.
  const groups = groupRowsByCategory(rows, checklist)

  // Every previewable file, flattened in the order the groups render. The
  // ticket is explicit that navigation runs "through each photo category
  // without stopping", so the sequence spans groups rather than resetting at
  // each boundary.
  const previewSequence = useMemo(
    () =>
      groups.flatMap((group) =>
        group.rows
          .filter(({ att }) => !att.is_deleted && (att.file_url || att.uuid))
          // Carry the group's label so the viewer can name the category the
          // reader is currently in - the sequence crosses them, and a filename
          // does not say which one this is.
          .map(({ att }) => ({ att, categoryLabel: group.label })),
      ),
    [groups],
  )
  // Photos open in the annotating reviewer (flags, notes, pins); anything else
  // has no coordinate to pin and stays in the plain file viewer. The split is
  // by medium, not by tenant.
  const photoSequence = useMemo(
    () => previewSequence.filter(({ att }) => isImageValue(att.mime_type, att.file_name)),
    [previewSequence],
  )
  const annotationMap = useMemo(() => {
    const map = new Map<string, EvidenceAnnotation>()
    // Straight from the API, and 404s behind the lab flag - so check the shape
    // rather than assume a list.
    if (!Array.isArray(annotations)) return map
    for (const ann of annotations) map.set(ann.evidence_file_uuid, ann)
    return map
  }, [annotations])

  const openPreview = (att: Attachment) => {
    const photoIdx = photoSequence.findIndex((entry) => entry.att.uuid === att.uuid)
    if (photoIdx >= 0) setLightboxIndex(photoIdx)
    else setPreviewing(att)
  }

  const previewIndex = previewing
    ? previewSequence.findIndex((entry) => entry.att.uuid === previewing.uuid)
    : -1
  const stepPreview = (delta: number) => {
    if (previewIndex < 0) return
    const next = previewSequence[previewIndex + delta]
    if (next) setPreviewing(next.att)
  }

  const toggleGroup = (key: string) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  // Download every (non-deleted) attachment on the claim as one zip. Hits the
  // authenticated endpoint as a blob so the session token rides along, then
  // triggers a single browser download.
  const handleDownloadAll = async () => {
    setDownloadingAll(true)
    try {
      const res = await api.get(
        `/files/attachments/claim/${claimUuid}/download-all/`,
        { responseType: 'blob' },
      )
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `claim-${claimUuid.slice(0, 8)}-evidence.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      toastApiError(err, "Couldn't download the evidence.")
    } finally {
      setDownloadingAll(false)
    }
  }

  return (
    <>
      <DetailCard
        title={title}
        icon={<ImageIcon className="h-3.5 w-3.5" />}
        trailing={
          <div className="flex items-center gap-3">
            {liveCount > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={handleDownloadAll}
                disabled={downloadingAll}
                className="inline-flex cursor-pointer items-center gap-1 bg-transparent p-0 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] transition-opacity duration-150 hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {downloadingAll ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Download className="h-3 w-3" />
                )}
                Download all
              </Button>
            ) : null}
            {deletedCount > 0 || showDeleted ? (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => setShowDeleted((v) => !v)}
                className="inline-flex cursor-pointer items-center gap-1 bg-transparent p-0 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] transition-opacity duration-150 hover:opacity-70"
              >
                {showDeleted
                  ? 'Hide deleted'
                  : `Show deleted${deletedCount ? ` (${deletedCount})` : ''}`}
              </Button>
            ) : null}
            {canAdd ? (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={() => setAddOpen(true)}
                className="inline-flex cursor-pointer items-center gap-1 bg-transparent p-0 text-[10px] font-normal uppercase tracking-[0.1em] text-[var(--green)] transition-opacity duration-150 hover:opacity-70"
              >
                <Plus className="h-3 w-3" />
                Add
              </Button>
            ) : null}
          </div>
        }
      >
        {query.isLoading || (filterToSection && isLoadingCats) ? (
          <div className="flex items-center justify-center py-6 text-[11px] text-[var(--ink-3)]">
            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
            Loading evidence…
          </div>
        ) : query.isError ? (
          <div className="px-3 py-4 text-center text-[11px] text-rose-600">
            Couldn't load evidence. Try refreshing.
          </div>
        ) : rows.length === 0 ? (
          <div className="px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
            No evidence attached
          </div>
        ) : (
          <ul className="divide-y divide-[var(--rule)]">
            {groups.map((group) => {
              const isCollapsed = !expandedGroups.has(group.key)
              return (
                <li key={group.key}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => toggleGroup(group.key)}
                    aria-expanded={!isCollapsed}
                    className="flex h-auto w-full items-center justify-start gap-2 rounded-none bg-[var(--bg-light)] px-3 py-2 hover:bg-[var(--bg-light)]"
                  >
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 shrink-0 text-[var(--ink-3)] transition-transform duration-150',
                        isCollapsed && '-rotate-90',
                      )}
                    />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-2)]">
                      {group.label}
                    </span>
                    {group.isApproved ? (
                      <Badge
                        variant="secondary"
                        className="text-[9px] uppercase tracking-wider"
                      >
                        {t('claims.evidence.approved', 'Approved')}
                      </Badge>
                    ) : group.isRequired ? (
                      <Badge
                        variant="outline"
                        className="text-[9px] uppercase tracking-wider"
                      >
                        {t('claims.evidence.required', 'Required')}
                      </Badge>
                    ) : null}
                    <span className="text-[10px] text-[var(--ink-3)]">
                      {group.rows.length}
                    </span>
                  </Button>
                  {!isCollapsed ? (
                    <ul className="divide-y divide-[var(--rule)] border-t border-[var(--rule)]">
                      {group.rows.map(({ att, deletable, source }) => (
                        <EvidenceRow
                          key={`${source}-${att.uuid}`}
                          att={att}
                          categoryLabel={group.label}
                          canDelete={deletable}
                          onPreview={() => openPreview(att)}
                          onRecategorise={
                            canRecategorise &&
                            source === 'registry' &&
                            !att.is_deleted
                              ? () => setRecategorising(att)
                              : undefined
                          }
                          onRequestDelete={() =>
                            setPendingDelete({ att, source })
                          }
                          isDeleting={
                            (deleteMutation.isPending &&
                              deleteMutation.variables?.uuid === att.uuid) ||
                            (removeEvidence.isPending &&
                              removeEvidence.variables?.file_uuid === att.uuid)
                          }
                        />
                      ))}
                    </ul>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}

        {showDeleted && deletedCount > 0 && liveCount === 0 ? (
          <div className="border-t border-[var(--rule)] px-3 py-2 text-[10px] text-[var(--ink-3)]">
            All evidence on this claim has been deleted.
          </div>
        ) : null}
      </DetailCard>

      <FileViewerDialog
        file={
          previewing
            ? ({
                file_uuid: previewing.uuid,
                file_url: previewing.file_url,
                file_name: previewing.file_name,
                mime_type: previewing.mime_type,
              } satisfies ViewableFile)
            : null
        }
        onOpenChange={(open) => {
          if (!open) setPreviewing(null)
        }}
        onPrevious={previewIndex > 0 ? () => stepPreview(-1) : undefined}
        onNext={
          previewIndex >= 0 && previewIndex < previewSequence.length - 1
            ? () => stepPreview(1)
            : undefined
        }
        position={
          previewIndex >= 0
            ? { index: previewIndex + 1, total: previewSequence.length }
            : undefined
        }
        caption={
          previewIndex >= 0
            ? previewSequence[previewIndex].categoryLabel
            : undefined
        }
      />
      {pendingDelete ? (
        <DeleteConfirmDialog
          attachment={pendingDelete.att}
          source={pendingDelete.source}
          onCancel={() => setPendingDelete(null)}
          onConfirm={(reason) => {
            const done = { onSuccess: () => setPendingDelete(null) }
            if (pendingDelete.source === 'registry') {
              deleteMutation.mutate(
                { uuid: pendingDelete.att.uuid, reason },
                done,
              )
            } else {
              // Attribute-values evidence: remove the file uuid from the claim's
              // file attributes (WP1-63). No soft-delete/reason on this path.
              // useRemoveClaimEvidence has no onError toast of its own (unlike
              // useDeleteAttachment), so surface failures here and keep the
              // confirm open for a retry rather than failing silently.
              removeEvidence.mutate(
                { uuid: claimUuid, file_uuid: pendingDelete.att.uuid },
                {
                  ...done,
                  onError: (err) =>
                    toastApiError(err, "Couldn't remove the evidence."),
                },
              )
            }
          }}
          isDeleting={isDeletingAny}
        />
      ) : null}
      {lightboxIndex !== null && photoSequence[lightboxIndex] ? (
        <PhotoLightbox
          files={photoSequence.map(({ att }) => ({
            uuid: att.uuid,
            file_url: att.file_url,
            file_name: att.file_name,
          }))}
          annotations={annotationMap}
          currentIndex={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
          claimUuid={claimUuid}
          readOnly={!canDelete}
          userRole="rs"
          annotationsEnabled={labEnabled}
          captionFor={(index) => photoSequence[index]?.categoryLabel}
        />
      ) : null}
      {recategorising ? (
        <RecategoriseDialog
          attachment={recategorising}
          groups={groups}
          checklist={checklist}
          isSaving={recategorise.isPending}
          onCancel={() => setRecategorising(null)}
          onConfirm={(category) =>
            recategorise.mutate(
              { uuid: recategorising.uuid, category },
              { onSuccess: () => setRecategorising(null) },
            )
          }
        />
      ) : null}
      {addOpen ? (
        <AddEvidenceDialog
          claimUuid={claimUuid}
          section={filterToSection}
          onClose={() => setAddOpen(false)}
        />
      ) : null}
    </>
  )
}

function EvidenceRow({
  att,
  categoryLabel,
  canDelete,
  onPreview,
  onRecategorise,
  onRequestDelete,
  isDeleting,
}: {
  att: Attachment
  /** The row's resolved category, named as the group above it names it. */
  categoryLabel: string
  canDelete: boolean
  onPreview: () => void
  /** Omitted for evidence that has no attachment row to move. */
  onRecategorise?: () => void
  onRequestDelete: () => void
  isDeleting: boolean
}) {
  const { formatDateTime } = useFormatters()
  const deleted = att.is_deleted

  return (
    <EvidenceFileRow
      fileName={att.file_name}
      fileUuid={att.uuid}
      fileUrl={att.file_url}
      thumbnailUrl={att.thumbnail_url}
      thumbnail={
        <AuthenticatedImage
          src={att.thumbnail_url ?? att.file_url}
          alt={att.file_name}
          className="h-full w-full object-cover"
          fallback={null}
        />
      }
      mimeType={att.mime_type}
      deleted={deleted}
      onPreview={onPreview}
      onRecategorise={onRecategorise}
      onDelete={canDelete ? onRequestDelete : undefined}
      isDeleting={isDeleting}
      badges={
        // One badge, from the same resolution the group header uses. A file
        // can carry both a category and a requirement key for the same thing,
        // which rendered as two badges - one of them the raw underscored key.
        categoryLabel ? (
          <Badge variant="secondary" className="text-[9px] uppercase tracking-wider">
            {categoryLabel}
          </Badge>
        ) : null
      }
      meta={
        <>
          <span>{formatBytes(att.file_size)}</span>
          <span>·</span>
          <span>{formatDateTime(att.uploaded_at)}</span>
          {att.uploader.kind || att.uploader.email ? (
            <>
              <span>·</span>
              <span>
                {RECIPIENT_LABEL[att.uploader.kind] ?? att.uploader.kind ?? 'Uploader'}
                {att.uploader.email ? ` (${att.uploader.email})` : ''}
              </span>
            </>
          ) : null}
        </>
      }
      deletedNote={
        <>
          Deleted{' '}
          {att.deleted_at ? `on ${formatDateTime(att.deleted_at)}` : ''}
          {att.deleted_by_email ? ` by ${att.deleted_by_email}` : ''}
          {att.deleted_reason ? ` - ${att.deleted_reason}` : ''}
        </>
      }
    />
  )
}

function DeleteConfirmDialog({
  attachment,
  source,
  onCancel,
  onConfirm,
  isDeleting,
}: {
  attachment: Attachment
  source: EvidenceSource
  onCancel: () => void
  onConfirm: (reason: string) => void
  isDeleting: boolean
}) {
  // Mounted already-open by the parent (which only renders this when an
  // attachment is targeted) and unmounted on close - the same pattern the
  // Add dialog uses. Toggling `open` on an always-mounted Base UI dialog
  // does not reliably close it, which left Cancel stuck.
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !isDeleting) onCancel()
      }}
    >
      <DeleteConfirmBody
        attachment={attachment}
        source={source}
        onCancel={onCancel}
        onConfirm={onConfirm}
        isDeleting={isDeleting}
      />
    </Dialog>
  )
}

function DeleteConfirmBody({
  attachment,
  source,
  onCancel,
  onConfirm,
  isDeleting,
}: {
  attachment: Attachment | null
  source: EvidenceSource
  onCancel: () => void
  onConfirm: (reason: string) => void
  isDeleting: boolean
}) {
  const [reason, setReason] = useState('')
  // Registry attachments soft-delete (a reason is recorded on the tombstone).
  // Attribute-values evidence is unlinked from the claim's file fields - the
  // remove endpoint takes no reason, so we don't prompt for one.
  const isRegistry = source === 'registry'

  return (
    <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete evidence</DialogTitle>
          <DialogDescription>
            {attachment ? (
              <>
                You're about to delete{' '}
                <span className="font-medium text-[var(--ink)]">
                  {attachment.file_name}
                </span>
                {isRegistry
                  ? '. The file will be hidden from the claim but kept in storage for audit.'
                  : '. The file will be removed from the claim but kept in storage.'}
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        {isRegistry ? (
          <div className="space-y-2">
            <Label htmlFor="delete-reason">Reason (optional)</Label>
            <Input
              id="delete-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. duplicate upload, wrong file"
              disabled={isDeleting}
              autoFocus
            />
          </div>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => onConfirm(reason)}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                Deleting…
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogFooter>
    </DialogContent>
  )
}

/**
 * Move one already-uploaded file to a different category (WPS-140 #2).
 *
 * Offers what this claim can actually file under: the categories its packet
 * asks for, plus any category already in use on the claim - so a file can be
 * moved into an existing group without inventing a new one. The media-type
 * fallback groups are not offered; they mean "nobody classified this", which
 * is a state to move OUT of, not into.
 */
function RecategoriseDialog({
  attachment,
  groups,
  checklist,
  isSaving,
  onCancel,
  onConfirm,
}: {
  attachment: Attachment
  groups: CategoryGroup[]
  checklist: ClaimChecklist | undefined
  isSaving: boolean
  onCancel: () => void
  onConfirm: (category: string) => void
}) {
  const { t } = useTranslation()
  const current = resolveEvidenceCategoryKey(attachment)
  const [choice, setChoice] = useState('')

  const options = useMemo(() => {
    const byKey = new Map<string, string>()
    for (const item of checklist?.items ?? []) {
      const key = normaliseCategoryKey(item.category || item.key)
      if (key) byKey.set(key, item.label || key)
    }
    for (const item of checklist?.all_photo_categories ?? []) {
      const key = normaliseCategoryKey(item.category || item.key)
      if (key && !byKey.has(key)) byKey.set(key, item.label || key)
    }
    for (const group of groups) {
      if (group.key.startsWith(MEDIA_GROUP_PREFIX)) continue
      if (!byKey.has(group.key)) byKey.set(group.key, group.label)
    }
    return [...byKey.entries()]
      .filter(([key]) => key !== current)
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [checklist, groups, current])

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('claims.recategorise.title', 'Change category')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'claims.recategorise.description',
              "Move {{fileName}} out of {{currentCategory}} into another category. The claim's checklist updates to match.",
              {
                fileName: attachment.file_name,
                currentCategory: evidenceGroupLabel(current, new Map()),
              },
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[320px] overflow-y-auto">
          {options.length === 0 ? (
            <p className="px-1 py-3 text-[12px] text-[var(--ink-3)]">
              {t(
                'claims.recategorise.empty',
                'There is no other category to move this into yet.',
              )}
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {options.map((option) => (
                <li key={option.key}>
                  <Button
                    type="button"
                    // `secondary` paints the selected row through the design
                    // system rather than an override the ghost variant wins.
                    variant={choice === option.key ? 'secondary' : 'ghost'}
                    role="radio"
                    aria-checked={choice === option.key}
                    onClick={() => setChoice(option.key)}
                    className={cn(
                      'h-auto w-full justify-start gap-2 rounded-md px-3 py-2 text-left text-[13px]',
                      choice === option.key &&
                        'font-medium ring-1 ring-[var(--accent-foreground)]',
                    )}
                  >
                    <Check
                      className={cn(
                        'h-3.5 w-3.5 shrink-0',
                        choice === option.key
                          ? 'text-[var(--accent-foreground)]'
                          : 'invisible',
                      )}
                    />
                    {option.label}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            {t('common.cancel', 'Cancel')}
          </Button>
          {/* Enabled with nothing picked: the click surfaces which choice is
              missing rather than leaving a dead button. */}
          <Button onClick={() => choice && onConfirm(choice)} disabled={isSaving}>
            {isSaving
              ? t('claims.recategorise.moving', 'Moving...')
              : t('claims.recategorise.move', 'Move')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
